// Quote mode's step 4: pick the carriers to build plans from.
//
// The RFP has its own step now (Step4Recipients), because it has to choose who
// receives the RFP as well as which carriers to name. Quote mode has neither
// recipients nor general agencies, so it is only ever this list.

import { useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import type { Carrier } from "@shared/schema";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { CarrierListByLine } from "./CarrierListByLine";
import { useCarrierAvailability } from "./useCarrierAvailability";
import { pickedCarrierIds } from "@shared/utils/rfpGaCarriers";
import { getCoverageLabel } from "../constants";

export function Step4SelectCarriers() {
  const { quoteData, updateQuoteData, quotableCarriersLoading } = useQuoteWizardContext();
  const { entityState, getCarrierCoverageTypes, getAvailableCarriers } = useCarrierAvailability();

  const picked = pickedCarrierIds(quoteData.selectedCarriers);

  // Ticking a carrier applies to every line it can be quoted for, so a carrier
  // is simply in or out of the quote rather than in for dental and out for
  // vision. It appears under each of its lines and they move together.
  const toggleCarrier = useCallback(
    (carrier: Carrier, fromCoverageType: string) => {
      const carrierId = carrier.id;
      if (!carrierId) return;

      const isCurrentlySelected = (
        quoteData.selectedCarriers[fromCoverageType] || []
      ).includes(carrierId);
      const updatedSelections = { ...quoteData.selectedCarriers };

      for (const coverageType of getCarrierCoverageTypes(carrier)) {
        const currentCarriers = updatedSelections[coverageType] || [];
        updatedSelections[coverageType] = isCurrentlySelected
          ? currentCarriers.filter((id) => id !== carrierId)
          : currentCarriers.includes(carrierId)
            ? currentCarriers
            : [...currentCarriers, carrierId];
      }

      updateQuoteData({ selectedCarriers: updatedSelections });
    },
    [quoteData.selectedCarriers, getCarrierCoverageTypes, updateQuoteData],
  );

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Select Carriers</h3>
        <p className="text-muted-foreground">Choose carriers for each coverage type</p>
        {entityState && (
          <span className="block text-sm mt-1 text-muted-foreground">
            Showing carriers with plans and rates available in{" "}
            <Badge variant="outline" className="ml-1">{entityState}</Badge>
          </span>
        )}
      </div>

      <Card>
        <CardContent className="p-4">
          <CarrierListByLine
            coverageTypes={quoteData.coverageTypes}
            getCarriers={getAvailableCarriers}
            isTicked={(carrierId) => picked.includes(carrierId)}
            onToggle={toggleCarrier}
            loading={!!entityState && quotableCarriersLoading}
            emptyMessage={(coverageType) =>
              entityState
                ? `No carriers have ${getCoverageLabel(coverageType).toLowerCase()} plans with rates in ${entityState} yet. Add plans and rates in the Plan Library to quote this coverage.`
                : "No carriers found for this coverage type."
            }
          />
        </CardContent>
      </Card>
    </div>
  );
}
